/* eslint-disable @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars */
import {
  Fragment,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react'

import { Button } from '@wartek-id/button-v1'
import { Link } from '@wartek-id/link'
import { Separator } from '@wartek-id/separator'
import { Text } from '@wartek-id/text-v1'

import CardIndikatorDimensi from 'app/KinerjaKSPage/components/CardIndikatorDimensi'
import useContentContext from 'app/KinerjaKSPage/hooks/useContentContext'
import useSKPKSTrackEvent from 'app/KinerjaKSPage/hooks/useSKPKSTrackEvent'
import Icon from 'app/KinerjaKSPage/pages/Perencanaan/Tab1/components/Icon'

import type { SKPTabHandler } from 'types/kinerja'
import type { KinerjaKs, UpdateSKPKSPayloadWithoutNpsn } from 'types/kinerja-ks'

interface Tab1Props {
  skp: KinerjaKs
  isSubmitting?: boolean
  onClickNext?: (data: UpdateSKPKSPayloadWithoutNpsn) => void
  onClickSave?: (data: UpdateSKPKSPayloadWithoutNpsn) => void
}

// eslint-disable-next-line react/display-name
const Tab1 = forwardRef<SKPTabHandler, Tab1Props>(
  ({ skp, isSubmitting = false, onClickNext, onClickSave }, ref) => {
    const { trackClickDraftPlanning, trackClickToPengembanganKompetensi } =
      useSKPKSTrackEvent()

    const { contentKepemimpinanSatuanPendidikan } = useContentContext()

    // const { data: profile, isFetched: isProfileFetched } = useAPIKinerjaProfile()

    const [selectedIndicator, setSelectedIndicator] = useState<number | null>(
      skp?.kepemimpinanSatuanPendidikan?.[0]
    )

    const isDisabledNextButton = !selectedIndicator

    const handleSelected = (selected: number) => {
      setSelectedIndicator(selected)
    }

    // Field `npsn` can be safely ignored since it will be automatically added to the payload.
    const generatePayload = useCallback((): UpdateSKPKSPayloadWithoutNpsn => {
      return {
        kepemimpinanSatuanPendidikan: !!selectedIndicator
          ? [selectedIndicator]
          : [],
      }
    }, [selectedIndicator])

    const onSaveDraft = useCallback(() => {
      trackClickDraftPlanning({
        kinerjaPlanningId: skp.id,
        kinerjaPlanningStatus: skp.status,
        screenName: 'KINERJA_PLANNING_KS_PRAKTIK_KINERJA_PAGE',
      })

      onClickSave(generatePayload())
    }, [onClickSave, generatePayload, trackClickDraftPlanning, skp])

    const onSubmit = () => {
      trackClickToPengembanganKompetensi({
        kinerjaPlanningId: skp.id,
        kinerjaPlanningStatus: skp.status,
        screenName: 'KINERJA_PLANNING_KS_PRAKTIK_KINERJA_PAGE',
      })

      onClickNext(generatePayload())
    }

    useImperativeHandle(
      ref,
      () => ({
        saveDraft: onSaveDraft,
      }),
      [onSaveDraft]
    )

    return (
      <div>
        <div className="py-9 overflow-auto">
          <div className="px-9 flex flex-col gap-3">
            <div>
              <Text variant="heading-md">
                Pilih indikator untuk menjadi fokus peningkatan kinerja Anda
              </Text>

              <Text className="mt-1" color="subdued">
                Pilihan Anda merupakan aspek penting dalam peningkatan satuan
                pendidikan. Kami sediakan pilihan berdasarkan{' '}
                <Link
                  href="https://raporpendidikan.kemdikbud.go.id/login"
                  className="!px-0 !cursor-pointer"
                  target="_blank"
                  rel="noreferrer"
                >
                  Rapor Pendidikan
                </Link>
                .
              </Text>
            </div>

            <Text variant="heading-sm" className="mt-4">
              Berikut indikator dari dimensi Kepemimpinan Pembelajaran:
            </Text>
            <div className="space-y-4">
              {contentKepemimpinanSatuanPendidikan?.map((indikator, index) => {
                return (
                  <Fragment key={`available-indikator-${index}`}>
                    <CardIndikatorDimensi
                      icon={<Icon name={indikator?.iconName} />}
                      indikator={indikator}
                      isSelected={selectedIndicator === indikator?.id}
                      onSelected={(selected) => {
                        if (selected) {
                          handleSelected(indikator?.id)
                        } else {
                          handleSelected(null)
                        }
                      }}
                    />
                  </Fragment>
                )
              })}
            </div>
          </div>
        </div>

        <Separator />

        <div className="px-8 py-4 flex gap-5 justify-end">
          <Button
            size="lg"
            color="white"
            isLoading={isSubmitting}
            disabled={isDisabledNextButton || isSubmitting}
            onClick={onSaveDraft}
            data-testid="btn-save-draft"
          >
            Simpan draf
          </Button>

          <Button
            size="lg"
            color="black"
            isLoading={isSubmitting}
            disabled={isDisabledNextButton || isSubmitting}
            type="submit"
            data-testid="btn-next"
            onClick={onSubmit}
          >
            Ke Pengembangan Kompetensi
          </Button>
        </div>
      </div>
    )
  }
)

export default Tab1
